/* ============================================================
   AndroidInspector.jsx — Inspector phone untuk ADMIN (tab dalam /android-test)
   ============================================================
   Keputusan user 4 Nov 2026: "inspector macam GenFarmer" untuk admin bina +
   baiki default robot phone (TikTok / Facebook / Shopee) dengan laju.
   - Skrin phone hidup → klik pada gambar = pilih elemen (bukan tekan phone),
     mod "Tap" = tekan phone sebenar.
   - Panel sifat: text · content-desc · resource-id · class · bounds · nisbah ·
     cadangan selector + bilangan padanan (unik atau tidak).
   - Senarai elemen + carian ("Find by string") · Copy XML / Copy selector.
   - Skrin beranimasi (feed video) tak boleh dibaca → klik tunjuk NISBAH sahaja.
   Logik pilih elemen = utils/androidInspect.js (dikongsi dengan mod "Ajar robot"
   pelanggan nanti). Satu tab hidup pada satu masa → satu sambungan phone.
   ============================================================ */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Btn, Badge } from '../design/ui';
import { AndroidController, lepaskanTelefon } from '../utils/androidAdb';
import { bacaSkrin, taipTepat, shell } from '../utils/androidDumpKit';
import { elementAt, klikPembungkus, cadangSelector, laluanRingkas, cariElemen } from '../utils/androidInspect';
import './AndroidInspector.css';

const APP_LAZIM = [
  { pkg: 'com.ss.android.ugc.trill', label: 'TikTok' },
  { pkg: 'com.facebook.katana', label: 'Facebook' },
  { pkg: 'com.shopee.my', label: 'Shopee' },
];

export default function AndroidInspector() {
  const ctlRef = useRef(null);
  const imgRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [shot, setShot] = useState('');
  const [els, setEls] = useState([]);
  const [xml, setXml] = useState('');
  const [dumpErr, setDumpErr] = useState('');
  const [sel, setSel] = useState(null);
  const [titik, setTitik] = useState(null); // {x,y} klik terakhir (piksel phone)
  const [tapMode, setTapMode] = useState(false);
  const [cari, setCari] = useState('');
  const [klikSahaja, setKlikSahaja] = useState(false);
  const [teks, setTeks] = useState('');
  const [fokus, setFokus] = useState('');
  const [pkg, setPkg] = useState(APP_LAZIM[0].pkg);

  const ctl = () => ctlRef.current;
  const skrin = { w: info?.screenWidth || 720, h: info?.screenHeight || 1640 };

  const refresh = useCallback(async () => {
    const c = ctlRef.current;
    if (!c?.connected) return;
    setBusy(true);
    // Skrin baharu → pilihan lama tidak sah lagi (elemen & kedudukan berubah).
    setSel(null); setTitik(null);
    try {
      const s = await c.screenshot();
      setShot(s.dataUrl);
      try {
        const raw = await c.dumpUi();
        setXml(raw);
        setEls(await bacaSkrin(c));
        setDumpErr('');
      } catch (e) {
        setEls([]); setXml('');
        setDumpErr(e?.message || String(e));
      }
      try { setFokus(String(await c.getFocusedWindow() || '').replace(/^.*\{[^ ]+ [^ ]+ /, '').replace(/\}$/, '')); } catch { setFokus(''); }
    } catch (e) {
      setMsg('Screenshot failed: ' + (e?.message || e));
    } finally { setBusy(false); }
  }, []);

  const sambung = async (baru) => {
    setBusy(true); setMsg('');
    try {
      if (!ctlRef.current) ctlRef.current = new AndroidController(() => {}, () => { setConnected(false); });
      if (baru) await ctlRef.current.connect(); else await ctlRef.current.reconnect();
      setInfo(await ctlRef.current.getDeviceInfo());
      setConnected(true);
      setBusy(false);
      await refresh();
    } catch (e) {
      setMsg(e?.code === 'NO_GRANTED' ? 'No phone allowed yet — press Connect.' : 'Connect failed: ' + (e?.message || e));
      setBusy(false);
    }
  };
  useEffect(() => () => { try { ctlRef.current?.disconnect(); } catch { /* biar */ } }, []);

  const lepas = async () => {
    try { await ctlRef.current?.disconnect(); } catch { /* biar */ }
    try { await lepaskanTelefon(); } catch { /* biar */ }
    setConnected(false); setMsg('Phone released.');
  };

  const keTelefon = (e) => {
    const img = imgRef.current;
    if (!img) return null;
    const r = img.getBoundingClientRect();
    const scale = Math.min(r.width / img.naturalWidth, r.height / img.naturalHeight);
    const offX = (r.width - img.naturalWidth * scale) / 2;
    const offY = (r.height - img.naturalHeight * scale) / 2;
    return { x: Math.round((e.clientX - r.left - offX) / scale), y: Math.round((e.clientY - r.top - offY) / scale) };
  };

  const klikSkrin = async (e) => {
    const p = keTelefon(e);
    if (!p) return;
    setTitik(p);
    if (tapMode && connected && !busy) {
      await ctl().tap(p.x, p.y);
      setTimeout(refresh, 900);
      return;
    }
    setSel(elementAt(els, p.x, p.y));
  };

  const aksi = async (f, tunggu = 900) => {
    if (!connected || busy) return;
    setBusy(true);
    try { await f(); } catch (e) { setMsg(e?.message || String(e)); }
    setBusy(false);
    setTimeout(refresh, tunggu);
  };
  const tekanPilihan = () => sel?.b && aksi(() => ctl().tap(Math.round((sel.b.x1 + sel.b.x2) / 2), Math.round((sel.b.y1 + sel.b.y2) / 2)));
  const taip = () => teks && aksi(async () => {
    const h = await taipTepat(ctl(), teks, {});
    setMsg(h.nota ? `Typed (check: ${h.nota})` : 'Typed.');
  }, 600);

  const salin = (v) => { try { navigator.clipboard.writeText(typeof v === 'string' ? v : JSON.stringify(v, null, 2)); setMsg('Copied.'); } catch { setMsg('Copy failed.'); } };

  const cadang = sel ? cadangSelector(sel, els, skrin) : null;
  const pembungkus = sel ? klikPembungkus(els, sel) : null;
  const senarai = cariElemen(els, cari, { klikSahaja }).slice(0, 300);

  // Kotak sorotan atas gambar (peratus supaya ikut saiz paparan).
  const kotak = (b, cls) => b && (
    <span className={cls} style={{
      left: `${(b.x1 / skrin.w) * 100}%`, top: `${(b.y1 / skrin.h) * 100}%`,
      width: `${((b.x2 - b.x1) / skrin.w) * 100}%`, height: `${((b.y2 - b.y1) / skrin.h) * 100}%`,
    }} />
  );

  return (
    <div className="ain">
      <div className="ain-bar">
        <Btn variant="primary" size="sm" onClick={() => sambung(true)} disabled={busy}>Connect</Btn>
        <Btn variant="ghost" size="sm" onClick={() => sambung(false)} disabled={busy}>Reconnect</Btn>
        <Btn variant="quiet" size="sm" onClick={lepas} disabled={!connected}>Release</Btn>
        <span className="ain-sep" />
        <select className="ain-select" value={pkg} onChange={(e) => setPkg(e.target.value)}>
          {APP_LAZIM.map((a) => <option key={a.pkg} value={a.pkg}>{a.label}</option>)}
        </select>
        <Btn variant="ghost" size="sm" disabled={!connected || busy} onClick={() => aksi(() => ctl().launchApp(pkg), 5000)}>Open app</Btn>
        <Btn variant="quiet" size="sm" disabled={!connected || busy} onClick={() => aksi(() => shell(ctl(), ['am', 'force-stop', pkg]), 800)}>Force stop</Btn>
        <span className="ain-grow" />
        {connected
          ? <Badge tone="ok">{info?.model || 'Phone'} · {skrin.w}×{skrin.h}</Badge>
          : <Badge>Not connected</Badge>}
      </div>
      {msg && <p className="ain-msg">{msg}</p>}

      <div className="ain-grid">
        {/* ── Skrin ── */}
        <section className="ain-col ain-col--screen">
          <div className="ain-tools">
            <Btn size="sm" variant="ghost" disabled={!connected || busy} onClick={refresh}>{busy ? 'Reading…' : 'Refresh'}</Btn>
            <Btn size="sm" variant="quiet" disabled={!connected || busy} onClick={() => aksi(() => ctl().back())}>Back</Btn>
            <Btn size="sm" variant="quiet" disabled={!connected || busy} onClick={() => aksi(() => ctl().home())}>Home</Btn>
            <Btn size="sm" variant="quiet" disabled={!connected || busy} onClick={() => aksi(() => ctl().swipe(skrin.w / 2, skrin.h * 0.75, skrin.w / 2, skrin.h * 0.35, 350))}>Swipe ↑</Btn>
            <Btn size="sm" variant="quiet" disabled={!connected || busy} onClick={() => aksi(() => ctl().swipe(skrin.w / 2, skrin.h * 0.35, skrin.w / 2, skrin.h * 0.75, 350))}>Swipe ↓</Btn>
            <label className="ain-toggle"><input type="checkbox" checked={tapMode} onChange={(e) => setTapMode(e.target.checked)} /> Tap phone on click</label>
          </div>
          <div className="ain-screen" onClick={klikSkrin}>
            {shot
              ? <>
                  <img ref={imgRef} src={shot} alt="Phone screen" draggable={false} />
                  <div className="ain-over">
                    {kotak(pembungkus && pembungkus !== sel ? pembungkus.b : null, 'ain-box ain-box--wrap')}
                    {kotak(sel?.b, 'ain-box')}
                    {titik && <span className="ain-dot" style={{ left: `${(titik.x / skrin.w) * 100}%`, top: `${(titik.y / skrin.h) * 100}%` }} />}
                  </div>
                </>
              : <div className="ain-empty">Connect a phone, then Refresh.</div>}
          </div>
          {fokus && <p className="ain-note">Window: {fokus}</p>}
          {dumpErr && <p className="ain-warn">This screen can&apos;t be read (usually a playing video). Clicks show the position ratio only.</p>}
        </section>

        {/* ── Sifat elemen ── */}
        <section className="ain-col">
          <h3 className="ain-h">Element</h3>
          {!sel && !titik && <p className="ain-note">Click an element on the screen, or pick one from the list.</p>}
          {titik && <p className="ain-note">Point: {titik.x}, {titik.y} · ratio {(titik.x / skrin.w).toFixed(4)}, {(titik.y / skrin.h).toFixed(4)}</p>}
          {sel && (
            <>
              <table className="ain-props">
                <tbody>
                  {[
                    ['text', sel.text], ['content-desc', sel.desc], ['resource-id', sel.id], ['class', sel.cls],
                    ['clickable', String(sel.clickable)], ['checked', String(sel.checked)], ['selected', String(sel.selected)],
                    ['bounds', `[${sel.b.x1},${sel.b.y1}][${sel.b.x2},${sel.b.y2}]`],
                    ['center ratio', `${(((sel.b.x1 + sel.b.x2) / 2) / skrin.w).toFixed(4)}, ${(((sel.b.y1 + sel.b.y2) / 2) / skrin.h).toFixed(4)}`],
                    ['XPathLite', laluanRingkas(sel)],
                  ].map(([k, v]) => <tr key={k}><th>{k}</th><td>{v || <span className="ain-dim">—</span>}</td></tr>)}
                </tbody>
              </table>
              {pembungkus && pembungkus !== sel && (
                <p className="ain-note">Tappable parent: {pembungkus.desc || pembungkus.text || pembungkus.id || pembungkus.cls}
                  {' '}<button type="button" className="ain-link" onClick={() => setSel(pembungkus)}>select</button></p>
              )}
              {cadang && (
                <div className="ain-sel">
                  <div className="ain-sel__h">Suggested selector
                    <Badge tone={cadang.unik === 1 ? 'ok' : 'warn'}>{cadang.unik === 1 ? 'unique' : cadang.jenis === 'ratio' ? 'position' : `${cadang.unik} matches`}</Badge>
                  </div>
                  <code>{cadang.jenis}: {typeof cadang.nilai === 'string' ? cadang.nilai : JSON.stringify(cadang.nilai)}</code>
                  {cadang.nota && <p className="ain-note">{cadang.nota}</p>}
                  <div className="ain-tools">
                    <Btn size="sm" variant="ghost" onClick={() => salin({ [cadang.jenis]: cadang.nilai })}>Copy selector</Btn>
                    <Btn size="sm" variant="primary" disabled={!connected || busy} onClick={tekanPilihan}>Tap this element</Btn>
                  </div>
                </div>
              )}
            </>
          )}
          <h3 className="ain-h">Type text</h3>
          <p className="ain-note">Tap the field first. Types exactly (keyboard auto-correct bypassed).</p>
          <div className="ain-tools">
            <input className="ain-input" value={teks} onChange={(e) => setTeks(e.target.value)} placeholder="Text to type" />
            <Btn size="sm" variant="ghost" disabled={!connected || busy || !teks} onClick={taip}>Type</Btn>
          </div>
        </section>

        {/* ── Senarai elemen ── */}
        <section className="ain-col">
          <div className="ain-tools">
            <input className="ain-input" value={cari} onChange={(e) => setCari(e.target.value)} placeholder="Find by string" />
            <Btn size="sm" variant="quiet" disabled={!xml} onClick={() => salin(xml)}>Copy XML</Btn>
          </div>
          <label className="ain-toggle"><input type="checkbox" checked={klikSahaja} onChange={(e) => setKlikSahaja(e.target.checked)} /> Tappable only</label>
          <p className="ain-note">{els.length} elements · showing {senarai.length}</p>
          <ul className="ain-list">
            {senarai.map((e, i) => (
              <li key={i}>
                <button type="button" className={'ain-row' + (sel === e ? ' ain-row--on' : '')} onClick={() => setSel(e)}>
                  <span className="ain-row__main">{e.text || e.desc || e.id || e.cls}</span>
                  <span className="ain-row__sub">{e.cls}{e.id ? ` · ${e.id}` : ''}{e.clickable ? ' · tap' : ''}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
